import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthImage } from "@/components/AuthMedia";
import { ArrowLeft, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * Bilingual A5 print/rework slip for an escalation.
 * The <ReworkSlip> component is used both on-screen (preview) and by window.print().
 *
 * Layout: side-by-side EN + HI columns at ~148 × 210 mm (A5 portrait).
 */
export default function ReworkSlip() {
  const { escId } = useParams();
  const nav = useNavigate();
  const [esc, setEsc] = useState(null);
  const [ins, setIns] = useState(null);
  const [po, setPo] = useState(null);
  const [loom, setLoom] = useState(null);
  const [weaver, setWeaver] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      // Escalations don't have direct GET-by-id endpoint; fetch list and find
      const all = await api.get("/escalations");
      const e = all.data.find((x) => x.id === escId);
      if (!e) return;
      setEsc(e);
      const [insRes, looms] = await Promise.all([
        api.get("/inspections", { params: { loom_id: e.loom_id } }),
        api.get("/looms"),
      ]);
      const iRec = insRes.data.find((x) => x.id === e.source_inspection_id) || insRes.data[0];
      setIns(iRec);
      const l = looms.data.find((x) => x.id === e.loom_id);
      setLoom(l);
      if (e.po_id) {
        const pr = await api.get(`/pos/${e.po_id}`);
        setPo(pr.data);
        setWeaver(pr.data.weaver);
      }
    })();
  }, [escId]);

  const doPrint = () => {
    window.print();
  };

  if (!esc) return <div className="p-6 text-sm text-[#7C8E9E]">Loading…</div>;

  return (
    <div>
      {/* screen-only toolbar */}
      <div className="print:hidden max-w-[900px] mx-auto p-4 flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => nav(-1)} className="text-sm flex items-center gap-1 text-[#4A627A]">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {esc.status !== "Resolved" && (
            <button
              data-testid="rework-complete-btn"
              onClick={async () => {
                const note = window.prompt("Confirmation note (short):", "Rework verified against slip; loom returned to Active.");
                if (!note) return;
                try {
                  await api.patch(`/escalations/${esc.id}`, { status: "Resolved", resolution_note: note });
                  alert("Escalation resolved. Loom reactivated.");
                  const r = await api.get("/escalations");
                  setEsc(r.data.find((x) => x.id === esc.id) || esc);
                } catch (e) {
                  alert(e?.response?.data?.detail || "Failed");
                }
              }}
              className="h-10 px-4 rounded-md font-semibold text-white flex items-center gap-2"
              style={{ background: "#0f766e" }}
            >
              Mark rework complete
            </button>
          )}
          {esc.status === "Resolved" && (
            <span className="status-chip pace-green">Resolved · {esc.resolved_at ? new Date(esc.resolved_at).toLocaleString() : ""}</span>
          )}
          <button
            data-testid="rework-print-btn"
            onClick={doPrint}
            className="h-10 px-4 rounded-md font-semibold text-white flex items-center gap-2"
            style={{ background: "var(--brand-navy)" }}
          >
            <Printer size={16} /> Print (A5)
          </button>
        </div>
      </div>

      {/* print slip */}
      <div ref={printRef} className="rework-slip mx-auto bg-white text-[#1B3A5C] relative overflow-hidden">
        {/* HAG monogram watermark */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center select-none"
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "180pt",
            fontWeight: 900,
            color: "var(--brand-navy)",
            opacity: 0.045,
            transform: "rotate(-18deg)",
            letterSpacing: "-0.05em",
          }}
        >
          HAG
        </div>
        <div className="relative">
          <SlipHeader esc={esc} po={po} loom={loom} />
          <div className="grid grid-cols-2 gap-6 mt-4">
            <SlipColumn lang="en" heading="Rework Instruction" esc={esc} ins={ins} po={po} loom={loom} weaver={weaver} />
            <SlipColumn lang="hi" heading="सुधार निर्देश" esc={esc} ins={ins} po={po} loom={loom} weaver={weaver} />
          </div>
          <SlipPhotos ins={ins} />
          <SlipFooter esc={esc} />
        </div>
      </div>

      {/* print CSS */}
      <style>{`
        .rework-slip {
          width: 148mm;
          min-height: 210mm;
          padding: 12mm;
          box-sizing: border-box;
          font-family: 'Manrope', system-ui, sans-serif;
          font-size: 10pt;
          line-height: 1.35;
          border: 1px solid rgba(27,58,92,0.15);
        }
        .rework-slip .lang-hi {
          font-family: 'Mukta', 'Manrope', system-ui, sans-serif;
        }
        @media print {
          @page { size: A5 portrait; margin: 6mm; }
          body { background: #fff !important; }
          .rework-slip { border: 0; padding: 0; margin: 0; }
        }
      `}</style>
    </div>
  );
}

function SlipHeader({ esc, po, loom }) {
  return (
    <div className="flex items-start justify-between border-b-2 pb-3" style={{ borderColor: "var(--brand-navy)" }}>
      <div className="flex items-start gap-3">
        {/* HAG monogram badge */}
        <div
          className="w-10 h-10 flex-shrink-0 rounded flex items-center justify-center"
          style={{ background: "var(--brand-navy)", color: "var(--brand-gold)" }}
        >
          <span className="font-black text-[16pt]" style={{ fontFamily: "'Fraunces', serif" }}>H</span>
        </div>
        <div>
          <div className="text-[8pt] uppercase tracking-[0.25em]" style={{ color: "var(--brand-gold)" }}>HAG Carpets · Rework Slip</div>
          <div className="text-[16pt] font-black leading-none mt-1" style={{ fontFamily: "'Fraunces', serif" }}>
            {loom?.loom_number || "—"} <span className="text-[10pt] font-normal text-[#4A627A]">· {po?.po_number || ""}</span>
          </div>
          <div className="text-[8pt] text-[#4A627A] mt-1">Raised: {new Date(esc.raised_at).toLocaleString()}</div>
        </div>
      </div>
      <div className="text-right">
        <div className={`inline-block px-2 py-1 rounded text-[9pt] font-bold ${esc.class === "Critical" ? "grade-C" : esc.class === "Major" ? "grade-B" : "grade-A"}`}>
          {esc.class}
        </div>
        <div className="text-[8pt] mt-1 text-[#4A627A]">Slip #{esc.id.slice(0, 8).toUpperCase()}</div>
      </div>
    </div>
  );
}

function SlipColumn({ lang, heading, esc, ins, po, loom, weaver }) {
  const hi = lang === "hi";
  const L = hi
    ? {
        loom: "लूम",
        weaver: "बुनकर",
        village: "गाँव",
        po: "PO",
        design: "डिज़ाइन",
        milestone: "माइलस्टोन",
        percent: "% पूरा",
        defect: "दोष",
        instruction: "सुधार निर्देश",
        raised_by: "उठाया",
        signature: "बुनकर के हस्ताक्षर",
        supervisor_sig: "सुपरवाइज़र के हस्ताक्षर",
      }
    : {
        loom: "Loom",
        weaver: "Weaver",
        village: "Village",
        po: "PO",
        design: "Design",
        milestone: "Milestone",
        percent: "% Complete",
        defect: "Defect",
        instruction: "Rework Instruction",
        raised_by: "Raised by",
        signature: "Weaver signature",
        supervisor_sig: "Supervisor signature",
      };
  return (
    <div className={hi ? "lang-hi" : ""}>
      <div className="text-[9pt] font-bold uppercase tracking-widest border-b border-[#1B3A5C]/20 pb-1 mb-2" style={{ color: "var(--brand-navy)" }}>
        {heading}
      </div>
      <Row k={L.loom} v={loom?.loom_number} />
      <Row k={L.weaver} v={weaver?.name} />
      <Row k={L.village} v={loom?.village} />
      <Row k={L.po} v={po?.po_number} />
      <Row k={L.milestone} v={ins?.milestone} />
      <Row k={L.percent} v={ins ? `${ins.percent_complete}%` : "—"} />
      <Row k={L.defect} v={`${esc.class} — ${esc.description || "—"}`} />
      <div className="mt-3">
        <div className="text-[8pt] uppercase tracking-widest text-[#7C8E9E]">{L.instruction}</div>
        <div className="mt-1 min-h-[36pt] text-[10pt] border border-dashed border-[#1B3A5C]/40 rounded p-2 whitespace-pre-wrap">
          {esc.resolution_note || (hi ? "(सुपरवाइज़र यहाँ लिखेंगे)" : "(Supervisor to write here)")}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 text-[8pt] text-[#7C8E9E]">
        <SigLine label={L.signature} />
        <SigLine label={L.supervisor_sig} />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-2 py-0.5">
      <span className="text-[8pt] uppercase tracking-widest text-[#7C8E9E] w-[70pt] flex-shrink-0">{k}</span>
      <span className="text-[10pt] font-semibold">{v || "—"}</span>
    </div>
  );
}

function SigLine({ label }) {
  return (
    <div>
      <div className="border-b border-black h-[24pt]" />
      <div className="mt-1">{label}</div>
    </div>
  );
}

function SlipPhotos({ ins }) {
  const photos = (ins?.photos || []).filter((p) => p.file_id).slice(0, 3);
  if (photos.length === 0) return null;
  return (
    <div className="mt-4 border-t border-[#1B3A5C]/15 pt-3">
      <div className="text-[8pt] uppercase tracking-widest text-[#7C8E9E]">Evidence photos</div>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {photos.map((p, i) => (
          <div key={i} className="border border-[#1B3A5C]/10 rounded overflow-hidden">
            <AuthImage fileId={p.file_id} alt="evidence" className="w-full h-[70pt] object-cover" />
            <div className="text-[7pt] text-[#7C8E9E] px-1 py-0.5">GPS {p.gps_lat?.toFixed?.(3)} · {new Date(p.taken_at).toLocaleDateString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlipFooter({ esc }) {
  const qrUrl = `${window.location.origin}/rework-slip/${esc.id}`;
  return (
    <div className="mt-4 pt-3 border-t border-[#1B3A5C]/15 flex items-start justify-between gap-4">
      <div className="text-[7pt] text-[#7C8E9E] flex-1">
        <div className="font-bold text-[#1B3A5C]">HAG Carpets Pvt. Ltd. · Mirzapur, U.P.</div>
        <div className="mt-1">Generated {new Date().toLocaleString()}</div>
        <div className="mt-1">Return this slip signed with the completed rework. / यह पर्ची पूरा सुधार होने पर हस्ताक्षर सहित लौटाएँ।</div>
        <div className="mt-2 text-[6pt]">Scan the QR to open the live escalation record.</div>
      </div>
      <div className="flex flex-col items-center" data-testid="slip-qr">
        <QRCodeSVG value={qrUrl} size={72} bgColor="#ffffff" fgColor="#1B3A5C" level="M" includeMargin={false} />
        <div className="text-[6pt] text-[#7C8E9E] mt-1 font-mono">{esc.id.slice(0, 8).toUpperCase()}</div>
      </div>
    </div>
  );
}
